/**
 * 同期キュー(CLAUDE.md §4-4)
 *
 * この段階では「積む」ところまで。送信・指数バックオフ・サーバー側の
 * 重複排除は手順⑥(Workers API)で実装する。
 *
 * キューを先に作っておくのは、スタンプ取得の側から見て
 * 「保存したら必ず積む」という不変条件をいま確定させたいため。
 * 送信処理を後から足すのは容易だが、積み忘れの穴は後から塞ぎにくい。
 */

import data from "../data/checkpoints.json";
import type { CheckpointData } from "../lib/checkpoints";
import { deriveCpToken } from "../lib/cpcode";
import { SECRET_CP } from "./secrets";
import { getAll, put, del, STORE } from "./db";
import type { Stamp } from "./stamps";

const cps = data as unknown as CheckpointData;

export type QueueItem = StampQueueItem | ProofQueueItem;

export interface StampQueueItem {
  /** サーバー側の冪等キー。スタンプの ULID をそのまま使う。 */
  ulid: string;
  type: "stamp";
  payload: StampPayload;
  created_at: number;
  /** 送信試行回数。指数バックオフの計算に使う。 */
  attempts: number;
}

/**
 * 達成証明の登録。
 *
 * これを送っておかないと、受付で8文字だけを手入力されたときに
 * サーバーが照合する相手を持てない(§9-5)。
 */
export interface ProofQueueItem {
  /** キューの主キー。証明コードをそのまま使う(1端末1証明なので衝突しない)。 */
  ulid: string;
  type: "proof";
  payload: { code: string; payload: string };
  created_at: number;
  attempts: number;
}

/** サーバーへ送る形。device_id は送信時に付ける(§12) */
export interface StampPayload {
  ulid: string;
  cp_id: string;
  ts: number;
  lat: number | null;
  lng: number | null;
  acc: number | null;
  method: string;
  flags: string[];
  /**
   * そのCPの署名トークン。サーバー側でも検証する(§9-4)。
   *
   * 合言葉で取得した場合もここに入れる。アプリは secret_cp を持っているので
   * 取得経路に関わらずトークンを導出できる。経路ごとに検証の有無が変わると、
   * 「method を word と偽れば署名検証を回避できる」穴になる。
   */
  token: string;
}

export async function enqueueStamp(stamp: Stamp): Promise<void> {
  const item: QueueItem = {
    ulid: stamp.ulid,
    type: "stamp",
    created_at: Date.now(),
    attempts: 0,
    payload: {
      ulid: stamp.ulid,
      cp_id: stamp.cp_id,
      ts: stamp.timestamp,
      lat: stamp.gps?.lat ?? null,
      lng: stamp.gps?.lng ?? null,
      acc: stamp.gps?.acc ?? null,
      method: stamp.method,
      flags: stamp.flags,
      token: await deriveCpToken(SECRET_CP, cps.event_id, stamp.cp_id),
    },
  };
  // keyPath が ulid なので、二重に積んでも上書きされるだけで増えない
  await put(STORE.syncQueue, item);
}

export async function enqueueProof(code: string, payload: string): Promise<void> {
  const item: ProofQueueItem = {
    ulid: `proof:${code}`,
    type: "proof",
    created_at: Date.now(),
    attempts: 0,
    payload: { code, payload },
  };
  await put(STORE.syncQueue, item);
}

export async function getQueue(): Promise<QueueItem[]> {
  return getAll<QueueItem>(STORE.syncQueue);
}

export async function dequeue(ulid: string): Promise<void> {
  await del(STORE.syncQueue, ulid);
}

export async function queueLength(): Promise<number> {
  return (await getQueue()).length;
}
